import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {SelectCustomEvent} from '@ionic/angular';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {LavorazioniService} from '../../../_services/lavorazioni.service';
import {Lavorazione} from '../../../_models/Lavorazione';
import {SearchResult} from '../../../_models/SearchResult';
import {SaveMessage} from '../../../_helpers/save-message';
import {Subscription} from 'rxjs';
import {skip} from 'rxjs/operators';

@Component({
  selector: 'app-select-lavorazione',
  templateUrl: './select-lavorazione.component.html',
  styleUrls: ['./select-lavorazione.component.scss'],
  standalone: false
})
export class SelectLavorazioneComponent implements OnInit, OnDestroy {
  @Input() byCode = false;
  @Input() isString = false;
  @Input() lavorazioneValue: string;
  @Input() all = false;
  @Input() showLabel = true;
  @Input() excludedLavorazioneIds: number[] = [];
  @Input() multiple = false;
  @Input() lavorazioneValues: (string | number)[] = [];
  @Output() lavorazioneValueChange = new EventEmitter<string>();
  @Output() lavorazioneChange = new EventEmitter<Lavorazione>();
  @Output() lavorazioneValuesChange = new EventEmitter<(string | number)[]>();
  @Output() lavorazioniSelezionateChange = new EventEmitter<Lavorazione[]>();
  loading = false;
  lavoraziones = [];
  lavorazioneList: Lavorazione[] = [];
  myLavorazioniSubscription: Subscription;
  private accountSettingsSubscription: Subscription;

  constructor(
    private saveMessage: SaveMessage,
    private organizzazioniService: OrganizzazioniService,
    private lavorazioniService: LavorazioniService) {
  }

  ngOnInit() {
    this.getLavorazioni();
    // skip(1): il caricamento iniziale è già gestito sopra, evita il doppio caricamento su init
    this.accountSettingsSubscription = this.organizzazioniService.currentAccountSettingsSubject
      .pipe(skip(1))
      .subscribe(() => this.getLavorazioni());
  }

  ngOnDestroy() {
    this.myLavorazioniSubscription?.unsubscribe();
    this.accountSettingsSubscription?.unsubscribe();
  }

  selectLavorazione = (e: SelectCustomEvent) => {
    if (this.multiple) {
      this.selectLavorazioniMultiple(e.target.value);
      return;
    }
    this.lavorazioneValue = this.isString ? e.target.value.toString() : e.target.value;
    this.lavorazioneValueChange.emit(this.lavorazioneValue);
    this.lavorazioneList.forEach((lavorazione: Lavorazione) => {
      if (this.matchLavorazione(lavorazione, this.lavorazioneValue)) {
        this.lavorazioneChange.emit(lavorazione);
      }
    });
  }

  private matchLavorazione = (lavorazione: Lavorazione, value: string | number): boolean =>
    (this.byCode && lavorazione.codice === value) || (!this.byCode && lavorazione.id === +value);

  private selectLavorazioniMultiple = (raw: any) => {
    const values: (string | number)[] = Array.isArray(raw) ? raw : [];
    this.lavorazioneValues = this.isString ? values.map((v) => v.toString()) : values;
    this.lavorazioneValuesChange.emit(this.lavorazioneValues);
    const selezionate = this.lavorazioneList.filter((lavorazione: Lavorazione) =>
      this.lavorazioneValues.some((value) => this.matchLavorazione(lavorazione, value)));
    this.lavorazioniSelezionateChange.emit(selezionate);
  };

  getLavorazioni = () => this.organizzazioniService
    .lavorazioniPersonal && !this.all ? this.getMyLavorazioni() : this.getLavorazioniLocalAsync();

  getMyLavorazioni = () => {
    this.myLavorazioniSubscription?.unsubscribe();
    this.myLavorazioniSubscription = this.lavorazioniService.myLavorazioni
      .subscribe((_lavorazioni: Lavorazione[]) => this.setLavorazioni(_lavorazioni ?? []));
  };

  getLavorazioniLocalAsync() {
    this.loading = true;
    this.lavoraziones = [];
    this.lavorazioniService
      .getLavorazioniFilteredAsync(this.organizzazioniService.organizzazioneId, '', 0, 0, 'codice', true, true)
      .then((_lavorazioni: SearchResult<Lavorazione>) => this.setLavorazioni(_lavorazioni.result))
      .catch((_err) => this.saveMessage.messageError("Ci sono problemi con il caricamento delle lavorazioni."))
      .finally(() => this.loading = false);
  }

  setLavorazioni = (_result: Lavorazione[]) => {
    const excludedIds = new Set((this.excludedLavorazioneIds ?? []).map((id: number) => Number(id)));
    this.lavorazioneList = (_result ?? []).filter((_lavorazione: Lavorazione) => !excludedIds.has(Number(_lavorazione.id)));
    this.lavoraziones = [];
    if (this.lavorazioneList.length > 0) {
      this.lavorazioneList.forEach((_lavorazione: Lavorazione) =>
        this.lavoraziones.push({
          value: this.byCode ? _lavorazione.codice : _lavorazione.id,
          label: `${_lavorazione.codice}: ${_lavorazione.descrizione}`
        }));
    }
  };
}
